import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
// import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { GetProjectResponse, useGetApiProjectsByProjectIdUsersQuery } from "api/generatedApi";
import { useScreenSize, ScreenSizeEnum } from "shared/use-screen-size";
import { DefaultBack2DashBoardNavigation } from "../navigation/default";
import { useToast } from "shared/toast/hooks/use-toast";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";
import { DesktopProjectUsers } from "./desktop-users";
import { MobileProjectUsers } from "./mobile-users";
import { useCreateProjectUser } from "./create/hooks/use-create-project-user";
import { Role, useRoleRestriction } from "shared/user-restrictions/use-role-restriction";

interface Props {
  project: GetProjectResponse;
}

export const DefaultProjectParticipants = (props: Props) => {
  const { project } = props;
  const { screenSize } = useScreenSize();
  const { t } = useTranslation();
  const tRef = useRef(t);
  const toast = useToast();
  const toastRef = useRef(toast);

  const { hasRole } = useRoleRestriction(project);
  const isOwner = hasRole([Role.Owner]);
  const isProjectManager = hasRole([Role.Manager]);
  const isAssistantOwner = hasRole([Role.AssistantOwner]);

  const { canCreateProjectUsers } = useDashboardRestrictions(project);
  const { data, error, isLoading } = useGetApiProjectsByProjectIdUsersQuery({ projectId: project?.id ?? "" });
  const [userEmails, setUserEmails] = useState<(string | null | undefined)[]>([]);
  const openCreateUser = useCreateProjectUser({ project, userEmails: userEmails ?? [] });

  useEffect(() => {
    if (data) {
      setUserEmails(data?.users?.map((user) => user.email) ?? []);
    }

    if (error) {
      if ("status" in error && error.status === 401) {
        return toastRef.current.error(tRef.current("dashboard.users.getUsers.errorUnauthorized"));
      }
      toastRef.current.error(tRef.current("dashboard.users.getUsers.error"));
    }
  }, [data, error, toastRef, tRef]);

  return (
    <Grid item xs={true} overflow={"auto"} padding={1} height={"100%"}>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "start" }}>
        <DefaultBack2DashBoardNavigation here={t("dashboard.links.users")} />
        {isLoading && (
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={100} />
          </Box>
        )}
        {!isLoading &&
          data?.users &&
          (data?.users ?? []).length > 0 &&
          (screenSize === ScreenSizeEnum.Mobile ? (
            <MobileProjectUsers users={data.users} project={project} isOwner={isOwner} isProjectManager={isProjectManager} isAssistantOwner={isAssistantOwner} />
          ) : (
            <DesktopProjectUsers users={data.users} project={project} isOwner={isOwner} isProjectManager={isProjectManager} isAssistantOwner={isAssistantOwner} currentUserAffiliateId={project.currentUserAffiliateId} />
          ))}
        {!isLoading && canCreateProjectUsers() && (
          <Fab
            sx={{
              position: "absolute",
              bottom: screenSize === ScreenSizeEnum.Mobile ? "70px" : "30px",
              right: screenSize === ScreenSizeEnum.Mobile ? "20px" : "10px",
              backgroundColor: "primary.dark",
            }}
            onClick={openCreateUser}
          >
            <AddCircleOutlineIcon fontSize="large" sx={{ color: "grey.100", pb: "2px", pr: "2px" }} />
          </Fab>
        )}
      </Box>
    </Grid>
  );
};
